import * as api from "../../api";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import decode from "jwt-decode";

import Logo from "../../images/logo.svg";

import { LOGOUT } from "../../constants/actionTypes";
import {
  ArrowDropDown,
  Close,
  DashboardOutlined,
  ExitToApp,
  Menu,
  SettingsOutlined,
} from "@material-ui/icons";

import Sidebar from "../../components/Sidebar/Sidebar";
import Index from "../../components/Admin/Index/Index";
import Pending from "../../components/Admin/Pending/Pending";
import Unassigned from "../../components/Admin/Unassigned/Unassigned";
import Ongoing from "../../components/Admin/Ongoing/Ongoing";
import Urgent from "../../components/Admin/Urgent/Urgent";
import Allocations from "../../components/Admin/Allocations/Allocations";
import Declined from "../../components/Admin/Declined/Declined";
import Completed from "../../components/Admin/Completed/Completed";
import Approved from "../../components/Admin/Approved/Approved";
import Unapproved from "../../components/Admin/Unapproved/Unapproved";
import Concluded from "../../components/Admin/Concluded/Concluded";
import NewRequests from "../../components/Admin/NewRequests/NewRequests";
import OngoingRequests from "../../components/Admin/OngoingRequests/OngoingRequests";
import ConcludedRequests from "../../components/Admin/ConcludedRequests/ConcludedRequests";

import { ALERT, COUNTER } from "../../constants/actionTypes";

import "./Admin.css";

const Admin = ({ match }) => {
  const dispatch = useDispatch();
  const section = match.params.section;
  const history = useHistory();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("auth")));

  const logout = useCallback(() => {
    if (!user) history.push("/auth/signin");

    setUser(null);
    dispatch({ type: LOGOUT });
  }, [dispatch, history, user]);

  const handleToDashboard = () => {
    if (user.role === "basic") history.push("/projects/index");
    if (user.role === "translator") history.push("/translator/profile");
    if (user.role === "admin") history.push("/admin/index");
  };

  useEffect(() => {
    const token = user?.token;

    if (token) {
      const decodedToken = decode(token);

      if (decodedToken.exp * 1000 < new Date().getTime()) logout();
    }

    if (!user) return history.replace("/auth/sign");

    if (user.role !== "admin") return history.replace("/not-found");
  }, [user, location, history, logout]);

  useEffect(() => {
    const fetchCounter = async () => {
      try {
        const { data } = await api.getCounter();
        dispatch({ type: COUNTER, payload: data });
      } catch (error) {
        if (error.response)
          dispatch({
            type: ALERT,
            payload: {
              severity: "error",
              message: error.response.data.message,
              duration: 5000,
              display: true,
            },
          });
        else
          dispatch({
            type: ALERT,
            payload: {
              severity: "error",
              message: "An unexpected error occurred",
              duration: 5000,
              display: true,
            },
          });
      }
    };

    if (user?.role === "admin") fetchCounter();
  }, [user, dispatch]);

  return (
    <>
      {showSidebar && (
        <div
          className="admin__overlay z-2"
          onClick={() => setShowSidebar(false)}
        ></div>
      )}
      {user?.role === "admin" && (
        <div className="admin d-flex">
          <aside style={{ left: showSidebar ? "0" : "" }} className="z-3">
            <Link to="/">
              <div className="navBar__logo m-10">
                <img src={Logo} alt="OneConverse" />
              </div>
            </Link>

            <Close
              className="sidebar__close position-absolute t-0 r-0 m-5"
              onClick={() => setShowSidebar(false)}
            />

            <Sidebar section={section} setShowSidebar={setShowSidebar} admin />
          </aside>
          <main className="py-10 pb-40">
            <div className="admin__topBar d-flex align-center justify-end mb-40 px-40 py-10">
              <Menu
                className="admin__logo"
                onClick={() => setShowSidebar(true)}
              />
              {user && (
                <details className="navBar__details position-relative">
                  <summary className="d-flex align-center list-none pointer">
                    <div className="holder__logo d-flex align-center justify-center font-subhead font-bolder rounded-50">
                      <span className="text-uppercase">
                        {user.name.split(" ")[0].charAt(0)}
                      </span>
                    </div>
                    <ArrowDropDown
                      className="icon-light-grey"
                      fontSize="large"
                    />
                  </summary>
                  <div className="navBar__drop position-absolute r-0 mt-20 z-3 box-shadow-lg">
                    <p className="text-capitalise font-big font-bolder text-center px-40 my-10">
                      Hi, {user.name.split(" ")[0]}
                    </p>
                    <div
                      className="d-flex align-center font-bold py-10 px-20 w-100 border-top-1 pointer"
                      onClick={handleToDashboard}
                    >
                      <DashboardOutlined fontSize="small" className="mr-20" />
                      <span>Your Dashboard</span>
                    </div>
                    <div
                      className="d-flex align-center font-bold py-10 px-20 w-100 border-top-1 pointer"
                      onClick={() => history.push("/settings")}
                    >
                      <SettingsOutlined fontSize="small" className="mr-20" />
                      <span>Settings</span>
                    </div>
                    <div
                      style={{ color: "crimson" }}
                      className="d-flex align-center font-bold py-10 px-20 w-100 border-top-1 pointer"
                      onClick={logout}
                    >
                      <ExitToApp fontSize="small" className="mr-20" />
                      <span>Log Out</span>
                    </div>
                  </div>
                </details>
              )}
            </div>
            <div className="admin__main px-40">
              {section === "index" && <Index />}
              {section === "pending" && <Pending />}
              {section === "unassigned" && <Unassigned />}
              {section === "urgent" && <Urgent />}
              {section === "ongoing" && <Ongoing />}
              {section === "allocations" && <Allocations />}
              {section === "declined" && <Declined />}
              {section === "completed" && <Completed />}
              {section === "verified" && <Approved />}
              {section === "unapproved" && <Unapproved />}
              {section === "concluded" && <Concluded />}
              {section === "new-requests" && <NewRequests />}
              {section === "ongoing-requests" && <OngoingRequests />}
              {section === "concluded-requests" && <ConcludedRequests />}
            </div>
          </main>
        </div>
      )}
    </>
  );
};

export default Admin;
